/**
 * Regras da partida (item 4.3): início com contagem, relógio de 8 min, gol (+10) com reposição pelo goleiro,
 * captura da Faísca (+30, encerra), faltas que dão pênalti, morte súbita de 60 s só com gols e fim de jogo.
 */
import {
  FOULS,
  KICKOFF_FORMATION,
  MATCH,
  MATCH_FLOW,
  NET,
  PITCH,
  hoopCenters,
  type Position,
  type Side,
} from "@besenliga/shared";
import { DEFENDS, inKeeperArea, type BallsState, type FoulKind, type PlayEvent } from "./balls.js";
import type { CollisionEvent } from "./collision.js";
import { recordStats, type AthleteMatchStats, type PendingPass } from "./stats.js";
import type { AthleteState, Team } from "./flight.js";
import { NO_INPUT, type InputCommand, type InputFrame } from "./input.js";

const ticks = (seconds: number) => Math.round(seconds * NET.simTickHz);
/** Tick que nunca chega: a Faísca não aparece (morte súbita é só com gols). */
const NEVER = 2147483647;

export type Phase = "inicio" | "jogo" | "gol" | "penalti" | "fim";
export type EndReason = "faisca" | "tempo" | "morte-subita" | "empate";

export interface PenaltyState {
  taker: string;
  keeper: string | null;
  team: Team;
  /** Já arremessou: daqui a pouco a cobrança acaba, com ou sem gol. */
  shotTicks: number | null;
}

export interface MatchState {
  phase: Phase;
  /** Ticks que faltam na fase de pausa atual (início, gol, pênalti). */
  phaseTicks: number;
  /** Ticks de tempo normal que faltam. */
  clockTicks: number;
  suddenDeath: boolean;
  suddenDeathTicks: number;
  score: Record<Team, number>;
  goals: Record<Team, number>;
  fouls: Record<Team, number>;
  winner: Team | "empate" | null;
  endReason: EndReason | null;
  penalty: PenaltyState | null;
  /** Ticks seguidos de cada atleta bloqueando a própria área sem ser goleiro. */
  blockTicks: Record<string, number>;
  /** Time que repõe a bola depois do gol. */
  restartTeam: Team | null;
  /** Números de cada atleta na partida (item 4.5). */
  stats: Record<string, AthleteMatchStats>;
  pendingPass: PendingPass | null;
}

export function createMatch(): MatchState {
  return {
    phase: "inicio",
    phaseTicks: ticks(MATCH_FLOW.kickoffSeconds),
    clockTicks: ticks(MATCH.durationSeconds),
    suddenDeath: false,
    suddenDeathTicks: 0,
    score: { casa: 0, fora: 0 },
    goals: { casa: 0, fora: 0 },
    fouls: { casa: 0, fora: 0 },
    winner: null,
    endReason: null,
    penalty: null,
    blockTicks: {},
    restartTeam: null,
    stats: {},
    pendingPass: null,
  };
}

/** Ticks de jogo já disputados no tempo normal (o relógio da Faísca). */
export function elapsedTicks(m: MatchState): number {
  return ticks(MATCH.durationSeconds) - m.clockTicks;
}

/** Põe cada atleta na posição de saída do seu time, parado e olhando para o campo adversário. */
export function placeKickoff(athletes: AthleteState[]): void {
  const used: Record<Team, Partial<Record<Position, number>>> = { casa: {}, fora: {} };
  for (const a of athletes) {
    const slot = used[a.team][a.role] ?? 0;
    used[a.team][a.role] = slot + 1;
    const spots = KICKOFF_FORMATION[a.role];
    const spot = spots[slot % spots.length] ?? [-20, 10, 0];
    // Vagas repetidas (mais atletas que posições) vão um pouco para o lado.
    const extra = Math.floor(slot / spots.length) * 4;
    const side = DEFENDS[a.team];
    // A formação é do mandante (lado x < 0); o visitante é o espelho.
    a.position = { x: -side * spot[0], y: spot[1], z: spot[2] + extra };
    a.velocity = { x: 0, y: 0, z: 0 };
    a.heading = { x: -side, z: 0 };
    a.stunTicks = 0;
    a.chargeTicks = 0;
  }
}

/** Quem está parado pela regra neste tick: todos nas pausas; no pênalti, todos menos cobrador e goleiro. */
export function filterInputs(
  m: MatchState,
  inputs: InputFrame,
  athletes: readonly AthleteState[],
): InputFrame {
  if (m.phase === "jogo") return inputs;
  const out: Record<string, InputCommand> = {};
  for (const a of athletes) {
    const free =
      m.phase === "penalti" && m.penalty && (a.id === m.penalty.taker || a.id === m.penalty.keeper);
    out[a.id] = free ? (inputs[a.id] ?? NO_INPUT) : NO_INPUT;
  }
  return out;
}

function endMatch(m: MatchState, reason: EndReason, plays: PlayEvent[]): void {
  m.phase = "fim";
  m.endReason = reason;
  m.winner = m.score.casa === m.score.fora ? "empate" : m.score.casa > m.score.fora ? "casa" : "fora";
  m.penalty = null;
  plays.push({ kind: "fim", athlete: "" });
}

/** Começa o pênalti a favor de `team`: cobrador na marca, segurando a bola, contra o goleiro de quem fez a falta. */
function startPenalty(
  m: MatchState,
  team: Team,
  athletes: AthleteState[],
  balls: BallsState,
  plays: PlayEvent[],
): void {
  const candidates = athletes
    .filter((a) => a.team === team)
    .sort((a, b) => {
      const ra = a.role === "artilheiro" ? 0 : 1;
      const rb = b.role === "artilheiro" ? 0 : 1;
      return ra - rb || b.stats.arremesso - a.stats.arremesso || (a.id < b.id ? -1 : 1);
    });
  const taker = candidates[0];
  if (!taker) return;
  const keeper = athletes.find((a) => a.team !== team && a.role === "goleiro") ?? null;
  const side = -DEFENDS[team] as Side;
  const mid = hoopCenters(side)[1] ?? { x: side * PITCH.hoopX, y: 18, z: 0 };
  taker.position = { x: side * (PITCH.hoopX - MATCH_FLOW.penaltyDistance), y: mid.y, z: 0 };
  taker.velocity = { x: 0, y: 0, z: 0 };
  taker.heading = { x: side, z: 0 };
  taker.stunTicks = 0;
  taker.chargeTicks = 0;
  if (keeper) {
    keeper.position = { x: side * (PITCH.hoopX - 3), y: mid.y, z: 0 };
    keeper.velocity = { x: 0, y: 0, z: 0 };
    keeper.heading = { x: -side, z: 0 };
    keeper.stunTicks = 0;
  }
  // Os outros esperam atrás do cobrador, longe dos aros, até a cobrança acabar.
  const behind = PITCH.hoopX - MATCH_FLOW.penaltyDistance - 6;
  for (const a of athletes) {
    if (a === taker || a === keeper) continue;
    if (side * a.position.x > behind)
      a.position = { x: side * behind, y: a.position.y, z: Math.max(-14, Math.min(14, a.position.z)) };
    a.velocity = { x: 0, y: 0, z: 0 };
  }
  balls.pelota.holder = taker.id;
  balls.pelota.lastTouch = taker.id;
  balls.pelota.lastTeam = team;
  balls.pelota.regrabTicks = 0;
  restPetardos(balls, MATCH_FLOW.penaltySeconds);
  m.phase = "penalti";
  m.phaseTicks = ticks(MATCH_FLOW.penaltySeconds);
  m.penalty = { taker: taker.id, keeper: keeper?.id ?? null, team, shotTicks: null };
  plays.push({ kind: "penalti", athlete: taker.id, team });
}

/** Tira as posses do tick (bola pega durante uma parada não vale). */
function dropPossession(plays: PlayEvent[]): void {
  for (let i = plays.length - 1; i >= 0; i--) if (plays[i]?.kind === "posse") plays.splice(i, 1);
}

/** Petardos de volta ao meio, descansando durante a parada e mais um pouco depois dela. */
function restPetardos(balls: BallsState, pauseSeconds: number): void {
  balls.petardos.forEach((p, i) => {
    p.position = { x: 0, y: 14, z: (i === 0 ? -1 : 1) * 10 };
    p.velocity = { x: 0, y: 0, z: 0 };
    p.mode = "descanso";
    p.modeTicks = ticks(pauseSeconds + MATCH_FLOW.restartPetardoRestSeconds);
    p.hitBy = null;
    p.ignore = null;
    p.ignoreTicks = 0;
  });
}

/** Bola com o goleiro do time `team` (ou solta no meio do lado dele, se não houver goleiro). */
function giveToKeeper(team: Team, athletes: AthleteState[], balls: BallsState): void {
  const keeper = athletes.find((a) => a.team === team && a.role === "goleiro" && a.stunTicks === 0);
  const pel = balls.pelota;
  pel.velocity = { x: 0, y: 0, z: 0 };
  pel.regrabTicks = 0;
  if (keeper && inKeeperArea(keeper)) {
    pel.holder = keeper.id;
    pel.lastTouch = keeper.id;
    pel.lastTeam = team;
  } else {
    pel.holder = null;
    pel.position = { x: DEFENDS[team] * (PITCH.hoopX - 12), y: 12, z: 0 };
  }
}

/** Faltas do tick: empurrão, bloqueio do aro e Petardo no goleiro dentro da área. Devolve a primeira. */
function detectFoul(
  m: MatchState,
  athletes: AthleteState[],
  collisions: readonly CollisionEvent[],
  plays: readonly PlayEvent[],
): { offender: string; fouled: Team; kind: FoulKind } | null {
  const byId = new Map(athletes.map((a) => [a.id, a]));
  let foul: { offender: string; fouled: Team; kind: FoulKind } | null = null;

  for (const c of collisions) {
    // Só o atropelo: quem empurrou vinha muito rápido na direção do outro (um choque de frente entre dois
    // atletas médios não é falta de ninguém).
    if (c.kind !== "atleta" || !c.other || !c.pusher || (c.pushSpeed ?? 0) < FOULS.pushSpeed) continue;
    const a = byId.get(c.athlete);
    const b = byId.get(c.other);
    if (!a || !b || a.team === b.team) continue;
    const pusher = c.pusher === a.id ? a : b;
    const victim = pusher === a ? b : a;
    foul ??= { offender: pusher.id, fouled: victim.team, kind: "empurrao" };
  }

  for (const p of plays) {
    if (p.kind !== "atordoado" || !p.other) continue;
    const victim = byId.get(p.athlete);
    const hitter = byId.get(p.other);
    if (
      victim?.role === "goleiro" &&
      hitter?.role === "batedor" &&
      hitter.team !== victim.team &&
      inKeeperArea(victim)
    )
      foul ??= { offender: hitter.id, fouled: victim.team, kind: "petardo-goleiro" };
  }

  // Bloquear o aro: quem não é goleiro, perto dos próprios aros, por tempo demais.
  const limit = ticks(FOULS.hoopBlockSeconds);
  for (const a of athletes) {
    let near = false;
    if (a.role !== "goleiro") {
      for (const h of hoopCenters(DEFENDS[a.team])) {
        const dx = a.position.x - h.x;
        const dy = a.position.y - h.y;
        const dz = a.position.z - h.z;
        if (dx * dx + dy * dy + dz * dz < FOULS.hoopBlockDistance * FOULS.hoopBlockDistance) near = true;
      }
    }
    const t = near ? (m.blockTicks[a.id] ?? 0) + 1 : 0;
    if (t > limit) {
      foul ??= { offender: a.id, fouled: a.team === "casa" ? "fora" : "casa", kind: "bloqueio" };
      m.blockTicks[a.id] = 0;
    } else m.blockTicks[a.id] = t;
  }
  return foul;
}

/**
 * Aplica as regras depois do voo, das colisões e das bolas. Altera `m`, `athletes` e `balls` (que já são cópias
 * deste tick) e acrescenta jogadas em `plays`. As jogadas de quando a bola está em jogo entram nas estatísticas.
 */
export function applyRules(
  m: MatchState,
  athletes: AthleteState[],
  balls: BallsState,
  collisions: readonly CollisionEvent[],
  plays: PlayEvent[],
): void {
  const live = m.phase === "jogo" || m.phase === "penalti";
  rulesTick(m, athletes, balls, collisions, plays);
  if (live) m.pendingPass = recordStats(m.stats, plays, athletes, balls, m.pendingPass);
}

/**
 * Aplica as regras depois do voo, das colisões e das bolas. Altera `m`, `athletes` e `balls` (que já são cópias
 * deste tick) e acrescenta jogadas em `plays`.
 */
function rulesTick(
  m: MatchState,
  athletes: AthleteState[],
  balls: BallsState,
  collisions: readonly CollisionEvent[],
  plays: PlayEvent[],
): void {
  const scored = (team: Team, points: number) => {
    m.score[team] += points;
  };

  if (m.phase === "fim") return;
  // A Faísca vale em qualquer fase: capturada, a partida acaba.
  const capture = plays.find((p) => p.kind === "captura");
  if (capture?.team) {
    scored(capture.team, MATCH.sparkCapturePoints);
    return endMatch(m, "faisca", plays);
  }

  switch (m.phase) {
    case "inicio": {
      // Todos parados e a bola esperando no meio, até o apito.
      balls.pelota.holder = null;
      dropPossession(plays);
      balls.pelota.position = { x: 0, y: 12, z: 0 };
      balls.pelota.velocity = { x: 0, y: 0, z: 0 };
      m.phaseTicks -= 1;
      if (m.phaseTicks <= 0) {
        m.phase = "jogo";
        plays.push({ kind: "inicio", athlete: "" });
      }
      return;
    }

    case "gol": {
      // Na comemoração ninguém pega a bola: ela espera no ar até a reposição.
      balls.pelota.holder = null;
      balls.pelota.velocity = { x: 0, y: 0, z: 0 };
      dropPossession(plays);
      m.phaseTicks -= 1;
      if (m.phaseTicks <= 0) {
        giveToKeeper(m.restartTeam ?? "casa", athletes, balls);
        m.restartTeam = null;
        m.phase = "jogo";
        plays.push({ kind: "reinicio", athlete: "" });
      }
      return;
    }

    case "penalti": {
      const pen = m.penalty;
      const goal = plays.find((p) => p.kind === "gol");
      if (pen && goal?.team) {
        scored(goal.team, MATCH.goalPoints);
        m.goals[goal.team] += 1;
        m.penalty = null;
        if (m.suddenDeath) return endMatch(m, "morte-subita", plays);
        m.phase = "gol";
        m.phaseTicks = ticks(MATCH_FLOW.goalPauseSeconds);
        restPetardos(balls, MATCH_FLOW.goalPauseSeconds);
        m.restartTeam = goal.team === "casa" ? "fora" : "casa";
        return;
      }
      if (pen && pen.shotTicks === null && balls.pelota.holder !== pen.taker) pen.shotTicks = ticks(2.5);
      if (pen && pen.shotTicks !== null) pen.shotTicks -= 1;
      m.phaseTicks -= 1;
      if (!pen || m.phaseTicks <= 0 || (pen.shotTicks !== null && pen.shotTicks <= 0)) {
        // Sem gol: a bola fica com o goleiro de quem se defendeu.
        const defending: Team = pen?.team === "casa" ? "fora" : "casa";
        giveToKeeper(defending, athletes, balls);
        restPetardos(balls, 0);
        m.penalty = null;
        m.phase = "jogo";
        plays.push({ kind: "reinicio", athlete: "" });
      }
      return;
    }

    case "jogo": {
      for (const p of plays) {
        if (p.kind === "gol" && p.team) {
          scored(p.team, MATCH.goalPoints);
          m.goals[p.team] += 1;
          if (m.suddenDeath) return endMatch(m, "morte-subita", plays);
          m.phase = "gol";
          m.phaseTicks = ticks(MATCH_FLOW.goalPauseSeconds);
          restPetardos(balls, MATCH_FLOW.goalPauseSeconds);
          m.restartTeam = p.team === "casa" ? "fora" : "casa";
          return;
        }
      }

      const foul = detectFoul(m, athletes, collisions, plays);
      if (foul) {
        const offender = athletes.find((a) => a.id === foul.offender);
        if (offender) m.fouls[offender.team] += 1;
        plays.push({ kind: "falta", athlete: foul.offender, team: foul.fouled, foul: foul.kind });
        startPenalty(m, foul.fouled, athletes, balls, plays);
        return;
      }

      if (m.suddenDeath) {
        m.suddenDeathTicks -= 1;
        if (m.suddenDeathTicks <= 0) endMatch(m, "empate", plays);
        return;
      }
      m.clockTicks -= 1;
      if (m.clockTicks <= 0) {
        if (m.score.casa !== m.score.fora) return endMatch(m, "tempo", plays);
        // Empate no tempo normal: 60 s de morte súbita, só com gols (a Faísca sai de campo).
        m.suddenDeath = true;
        m.suddenDeathTicks = ticks(MATCH.suddenDeathSeconds);
        balls.faisca.active = false;
        balls.faisca.appearTick = NEVER;
        plays.push({ kind: "morte-subita", athlete: "" });
      }
      return;
    }
  }
}
